import { createApp } from 'vue'; // Import createApp from 'vue'
import { createStore } from 'vuex'; // Import createStore from 'vuex'
import '../../utils/lightbox'; // Assuming this is side-effect import or utility setup
import { appCheck } from "../../utils/firebase/appCheck";
import { getToken } from "firebase/app-check";
import emitter from '../../utils/bus'; // Keeping custom bus for now
import { DestroyAttachment } from '../../utils/graphql/attachment-graph';
import graph_client from '../../utils/graphql/graph_client';
import {
  CreateReview, UpdateReview, DestroyReview, GetReview,
} from '../../utils/graphql/review-graph';
import axiosClient from '../../utils/helpers/axios_client';
import { OnBoardingGoal } from '../../utils/models/OnBoardingGoal';
import App from './App.vue'; // Your root component

// --- Vuex 4 Store Definition ---
const store = createStore({
  state() { // state is now a function
    return {
      showModal: false,
      loading: false,
      newReviewLoading: false,
      trailId: window.trailId, // Still reading from global scope initially
      editingReview: null,
      user: window.user, // Still reading from global scope initially
      trail: null,
      orderings: ['created', 'rating', 'ranking'],
      selectedOrder: 'ranking',
      body: '',
      rating: 5,
      uploading: false,
      clientId: `client_${Math.random().toString(36).substr(2, 9)}`,
      files: [],
      photoUrls: [],
      destroyingPhotoIds: [], // Consider if this needs to be reactive if UI depends on it during destruction
      pollingInterval: 3000, // Not used in the provided actions/getters, but kept
      error: null // Added for error handling in fetchTrail
    }
  },
  // --- Mutations ---
  // Mutations are synchronous functions responsible for *actually* changing state
  mutations: {
    SET_USER(state, user) {
      state.user = user;
    },
    SET_SHOW_MODAL(state, value) {
      state.showModal = value;
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_UPLOADING(state, value) {
      state.uploading = value;
    },
    SET_BODY(state, value) {
      state.body = value;
    },
    SET_RATING(state, value) {
      state.rating = value;
    },
    REMOVE_FILE(state, fileToRemove) {
      state.files = state.files.filter((f) => f.firebaseRefFullPath !== fileToRemove.firebaseRefFullPath);
    },
    ADD_DESTROYING_PHOTO_ID(state, photoId) {
      state.destroyingPhotoIds.push(photoId);
    },
    REMOVE_DESTROYING_PHOTO_ID(state, photoId) {
      state.destroyingPhotoIds = state.destroyingPhotoIds.filter(id => id !== photoId);
    },
    REMOVE_EDITING_REVIEW_PHOTO(state, photoId) {
      if (state.editingReview && state.editingReview.photos) {
        state.editingReview.photos = state.editingReview.photos.filter((p) => p.id !== photoId);
      }
    },
    RESET_REVIEW_FORM_STATE(state) {
      state.body = '';
      state.rating = 5;
      state.loading = false; // Also reset loading here?
      state.files = [];
      state.photoUrls = [];
      // Consider if destroyingPhotoIds should be reset here too
    },
    SET_TRAIL(state, trail) {
      state.trail = trail;
    },
    SET_ERROR(state, errorMessage) {
      state.error = errorMessage;
    },
    SET_EDITING_REVIEW(state, review) {
      state.editingReview = review;
      if (review) {
        state.body = review.body;
        state.rating = review.rating;
        // Reset temporary upload state when starting edit
        state.photoUrls = [];
        state.files = [];
      }
    },
    REMOVE_REVIEW_FROM_LIST(state, reviewId) {
      if (state.trail && state.trail.reviews) {
        state.trail.reviews = state.trail.reviews.filter((r) => r.id !== reviewId);
      }
    },
    UPDATE_REVIEW_IN_LIST(state, updatedReview) {
      if (state.trail && state.trail.reviews) {
        const index = state.trail.reviews.findIndex((r) => r.id === updatedReview.id);
        if (index !== -1) {
          // Use slice or Vue.set semantics if deep reactivity issues arise,
          // but direct assignment often works in Vue 3/Vuex 4
          state.trail.reviews.splice(index, 1, updatedReview);
        }
      }
    },
    ADD_REVIEW_TO_LIST(state, newReview) {
      if (state.trail && state.trail.reviews) {
        // Add to the beginning for 'created' order visibility?
        state.trail.reviews.unshift(newReview);
      } else if (state.trail) {
        state.trail.reviews = [newReview];
      }
    },
    UPDATE_PHOTO_IN_REVIEW(state, photo) {
      if (state.trail && state.trail.reviews) {
        const review = state.trail.reviews.find((r) => parseInt(r.id) === parseInt(photo.recordId));
        if (review && review.photos) {
          const index = review.photos.findIndex((p) => parseInt(p.id) === parseInt(photo.id));
          if (index !== -1) {
            review.photos.splice(index, 1, photo);
          }
        }
      }
    },
    SET_SELECTED_ORDER(state, order) {
      state.selectedOrder = order;
    },
  },
  // --- Actions ---
  // Actions commit mutations and can contain asynchronous operations
  actions: {
    // Simple actions just commit mutations
    setUser({ commit }, user) {
      commit('SET_USER', user);
    },
    setShowModal({ commit, dispatch }, value) {
      if (value) {
        dispatch('resetState'); // Reset state when opening modal (if intended)
      }
      commit('SET_SHOW_MODAL', value);
    },
    setUploading({ commit }, value) {
      commit('SET_UPLOADING', value);
    },
    setBody({ commit }, value) {
      commit('SET_BODY', value);
    },
    setRating({ commit }, value) {
      commit('SET_RATING', value);
    },
    removeFile({ commit }, file) {
      commit('REMOVE_FILE', file);
    },
    resetState({ commit }) {
      commit('RESET_REVIEW_FORM_STATE');
    },
    setSelectedOrder({ commit }, order) {
      commit('SET_SELECTED_ORDER', order);
    },

    // Actions with async logic
    destroyPhoto({ commit, state }, photo) {
      commit('ADD_DESTROYING_PHOTO_ID', photo.id);
      graph_client.request(DestroyAttachment, { attachment: { id: photo.id, recordId: photo.recordId, recordType: photo.recordType } })
        .then((response) => {
          const destroyedPhoto = response.destroyAttachment;
          commit('REMOVE_EDITING_REVIEW_PHOTO', destroyedPhoto.id);
        })
        .catch(error => {
          console.error("Error destroying photo:", error);
          // Optionally commit an error state or show a notification
        })
        .finally(() => {
          // Remove from the destroying list regardless of success/failure
          commit('REMOVE_DESTROYING_PHOTO_ID', photo.id);
        });
    },

    async fetchTrail({ state, commit, dispatch, getters }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null); // Reset error before fetching
      try {
        let headers = {};
        try {
          const appCheckTokenResponse = await getToken(appCheck, false);
          headers = { 'X-Firebase-AppCheck': appCheckTokenResponse.token };
        } catch (e) {
          console.error("Failed to get AppCheck token:", e);
          // Decide if you want to proceed without the token or show an error
        }
        const response = await axiosClient.get(getters.createGetTrailReviewsUrl, { headers });
        commit('SET_TRAIL', response.data.data.trail);
        dispatch('checkReviewUrl'); // Check URL after trail data is loaded
      } catch (error) {
        console.error("Error fetching trail data:", error);
        commit('SET_ERROR', `An error occurred while fetching data. ${error}`);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    checkReviewUrl({ state, commit }) {
      const url = new URL(window.location.href);
      const reviewId = url.searchParams.get('editing_review_id');

      if (reviewId) {
        if (state.user) { // Use state.user instead of window.user
          commit('SET_LOADING', true); // Indicate loading for GetReview
          graph_client.request(GetReview, { id: reviewId })
            .then((response) => {
              const { review } = response;
              if (review) {
                // Ensure it belongs to the current user? (Security check might be needed backend-side)
                if (review.user?.firebaseUserId === state.user?.firebaseUserId) { // Example check
                  commit('SET_EDITING_REVIEW', review);
                  commit('SET_SHOW_MODAL', true);
                } else {
                  console.warn("Attempted to edit review belonging to another user.");
                  // Optionally clear the URL parameter
                }
              } else {
                console.warn(`Review with ID ${reviewId} not found.`);
                // Optionally clear the URL parameter
              }
            })
            .catch(error => {
              console.error(`Error fetching review ${reviewId}:`, error);
            })
            .finally(() => {
              commit('SET_LOADING', false);
            });
        } else {
          // launch onboarding modal if reviewId exists but user is not logged in
          // Ensure window.onBoardingVue is still valid in Vue 3 context
          if (window.onBoardingVue && typeof window.onBoardingVue.launchFor === 'function') {
            window.onBoardingVue.launchFor(OnBoardingGoal.ReviewUpdate);
          } else {
            console.warn('onBoardingVue not available or launchFor method missing.');
          }
        }
      }
    },

    destroyReview({ state, commit }, review) {
      // confirm if the user wants to remove the review
      if (!confirm('Are you sure you want to delete this review?')) {
        return;
      }

      commit('ADD_DESTROYING_PHOTO_ID', review.id); // Reuse this state for visual feedback? Or create a new one?

      graph_client.request(DestroyReview, { review: { id: review.id } })
        .then((response) => {
          const destroyedReview = response.destroyReview;
          commit('REMOVE_REVIEW_FROM_LIST', destroyedReview.id);
          // If this review was being edited, close the modal and reset state
          if (state.editingReview && state.editingReview.id === destroyedReview.id) {
            commit('SET_SHOW_MODAL', false);
            commit('RESET_REVIEW_FORM_STATE');
            commit('SET_EDITING_REVIEW', null);
          }
        })
        .catch(error => {
          console.error("Error destroying review:", error);
          // Optionally show an error message
        })
        .finally(() => {
          commit('REMOVE_DESTROYING_PHOTO_ID', review.id); // Clean up
        });
    },

    addReview({ state, commit, dispatch }) {
      // User check should rely on state, which might be initially populated from window.user
      if (!state.user) {
        // launch onboarding modal
        if (window.onBoardingVue && typeof window.onBoardingVue.launchFor === 'function') {
          window.onBoardingVue.launchFor(OnBoardingGoal.Reviews);
        } else {
          console.warn('onBoardingVue not available or launchFor method missing.');
        }
      } else {
        commit('SET_EDITING_REVIEW', null); // Clear any previous edit state
        dispatch('resetState'); // Reset form fields
        commit('SET_SHOW_MODAL', true);
      }
    },

    editReview({ commit }, review) {
      commit('SET_EDITING_REVIEW', review); // This mutation now also sets body/rating and resets files/photoUrls
      commit('SET_SHOW_MODAL', true);
    },

    updatePhoto({ commit }, photo) {
      // This action seems to be called *after* a photo is uploaded/processed elsewhere?
      // It just updates the photo data within the review list in the state.
      commit('UPDATE_PHOTO_IN_REVIEW', photo);
    },

    // This action seems redundant if updatePhoto handles UI updates.
    // Maybe it was intended for direct list manipulation? Removing for now unless needed.
    // updateReview({ commit }, review) {
    //   commit('UPDATE_REVIEW_IN_LIST', review);
    // },

    // This seems like it should be part of submitReview's success logic
    // reviewCreated({ commit }, review) {
    //   commit('ADD_REVIEW_TO_LIST', review);
    // },

    async submitReview({ state, commit, dispatch }) {
      if (state.loading) return; // Prevent double submission

      state.newReviewLoading = true; // Set loading state for the new review submission
      commit('SET_LOADING', true);
      const params = {
        review: {
          trailId: state.trailId,
          body: state.body,
          rating: state.rating,
          photos: state.photoUrls, // Assuming photoUrls are populated correctly before submitting
        },
      };

      try {
        if (state.editingReview) {
          // --- Update existing review ---
          params.review.id = state.editingReview.id;
          const response = await graph_client.request(UpdateReview, params);
          // const updatedReview = response.updateReview;


          // Reload the page to ensure the latest data is fetched
          window.location.reload();
        } else {
          // --- Create new review ---
          const response = await graph_client.request(CreateReview, params);
          const createdReview = response.createReview;
          createdReview.fresh = true; // Add transient flag if needed by UI
          createdReview.checkForPhotos = params.review.photos.length > 0; // Add transient flag

          // Add the new review to the list (e.g., at the top)
          // commit('ADD_REVIEW_TO_LIST', createdReview);

          // Change sort order to show the new review first
          // commit('SET_SELECTED_ORDER', 'created'); // Use commit

          // Refetching after a delay might cause visual jumps. Consider if optimistic update is enough.
          // If polling or websockets are used, this might be unnecessary.
          setTimeout(() => {
            window.location.reload(); // Reload the page to ensure the latest data is fetched
          }, 3000);
        }

        // Close modal and reset form state on success
        commit('SET_SHOW_MODAL', false);
        dispatch('resetState'); // Use dispatch to call resetState action
        commit('SET_EDITING_REVIEW', null); // Clear editing state

      } catch (error) {
        console.error("Error submitting review:", error);
        commit('SET_ERROR', `Failed to submit review: ${error}`);
        // Potentially show error to user in the modal
      } finally {
        commit('SET_LOADING', false);
      }
    },
    // setSelectedOrder action already defined above
  },
  // --- Getters ---
  getters: {
    // Make sure state.trail and state.trail.reviews exist before sorting
    orderedReviews: (state) => {
      if (!state.trail || !state.trail.reviews) {
        return []; // Return empty array if data isn't loaded yet
      }
      // Create a shallow copy before sorting to avoid mutating state directly
      const reviewsCopy = [...state.trail.reviews];

      if (state.selectedOrder === 'rating') {
        return reviewsCopy.sort((a, b) => b.rating - a.rating);
      }
      if (state.selectedOrder === 'ranking') {
        // Handle potential null/undefined ranking values
        return reviewsCopy.sort((a, b) => (b.ranking ?? 0) - (a.ranking ?? 0));
      }
      // Default to 'created' (newest first)
      return reviewsCopy.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    },
    // Getter for the GraphQL URL remains the same logic
    createGetTrailReviewsUrl: (state) => {
      const query = 'query GetTrail($id: ID!) { trail(id: $id) { id name slug reviews { publicRecording { shareableUrl } id userId trailId body rating createdAt updatedAt ranking photos { lgUrl smUrl mdUrl metadata recordType recordId id } firebaseReviewId likes { id likeableType likeableId user { firebaseUserId } } user { firebaseUserId avatar { id lgUrl smUrl mdUrl metadata } title id displayName } } } }';
      const graph = `/graphql?query=${encodeURIComponent(query)}&variables=${encodeURIComponent(JSON.stringify({ id: state.trailId }))}`;
      return graph;
    },
    // Add getters for computed states if needed by components
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    isModalShown: (state) => state.showModal,
  },
});

// --- Vue 3 App Initialization ---
const app = createApp(App);

// Use the Vuex store
app.use(store);

// --- Global Event Bus Listener (Vue 3 style) ---
// Still relies on the external 'bus' and global window.axiosClient
// Consider refactoring bus logic into the store or using provide/inject if possible
emitter.on('user:updated', async () => {
  console.log("User updated event received"); // Debug log
  try {
    // Ensure axiosClient is correctly initialized
    if (!axiosClient) {
      console.error("axiosClient is not initialized.");
      return;
    }
    const response = await axiosClient.get('/user/current');
    const user = response.data;
    window.user = user; // Update global if other non-Vue parts rely on it
    // Dispatch action to update user in store
    store.dispatch('setUser', user);

    // Check the review URL again now that the user is potentially logged in
    if (store.state.user) { // Check store state
      store.dispatch('checkReviewUrl');
    }
  } catch (error) {
    console.error("Error fetching updated user:", error);
    store.commit('SET_ERROR', `Failed to update user: ${error}`);
  }
});


// Mount the app to the DOM
app.mount('#reviews');

// Expose the app instance globally IF absolutely necessary (generally discouraged)
// window.vueReviewsApp = app;
// window.vueReviewsStore = store;
